import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { authFetch } from '@/utils/auth-fetch';
import { useToast } from '@/components/ui/use-toast';
import { Search, Package, Filter, CheckSquare, Square } from 'lucide-react';

interface Product {
  id: string;
  product_name: string;
  category?: string;
  additional_data?: any;
}

interface ProductSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteId: string;
  sectorId: number;
  mode: 'research' | 'wheel';
  onConfirm: (params: {
    productIds: string[];
    maxProducts: number;
    language: string;
  }) => Promise<void>;
}

export function ProductSelectionModal({
  open,
  onOpenChange,
  siteId,
  sectorId,
  mode,
  onConfirm,
}: ProductSelectionModalProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [maxProducts, setMaxProducts] = useState(1000);
  const [language, setLanguage] = useState('en');

  // Load products when modal opens
  useEffect(() => {
    if (open && siteId) {
      loadProducts();
    }
  }, [open, siteId]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`/api/sites/${siteId}/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      } else {
        throw new Error('Failed to load products');
      }
    } catch (error) {
      console.error('Error loading products:', error);
      toast({
        title: 'Error',
        description: 'Failed to load products',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories from products
  const categories = React.useMemo(() => {
    const cats = new Set<string>();
    products.forEach((p) => {
      if (p.category) cats.add(p.category);
    });
    return Array.from(cats).sort();
  }, [products]);

  // Filter products based on search and category
  const filteredProducts = React.useMemo(() => {
    return products.filter((p) => {
      const matchesSearch =
        searchTerm === '' ||
        p.product_name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory =
        categoryFilter === 'all' || p.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [products, searchTerm, categoryFilter]);

  const handleSelectAll = () => {
    const newSelection = new Set(filteredProducts.map((p) => p.id));
    setSelectedProducts(newSelection);
  };

  const handleClearAll = () => {
    setSelectedProducts(new Set());
  };

  const handleToggleProduct = (productId: string) => {
    const newSelection = new Set(selectedProducts);
    if (newSelection.has(productId)) {
      newSelection.delete(productId);
    } else {
      newSelection.add(productId);
    }
    setSelectedProducts(newSelection);
  };

  const handleConfirm = async () => {
    if (selectedProducts.size === 0) {
      toast({
        title: 'No products selected',
        description: 'Please select at least one product',
        variant: 'destructive',
      });
      return;
    }

    setConfirming(true);
    try {
      await onConfirm({
        productIds: Array.from(selectedProducts),
        maxProducts,
        language,
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Error confirming selection:', error);
      toast({
        title: 'Error',
        description: 'Failed to start operation',
        variant: 'destructive',
      });
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {mode === 'research' ? 'Trigger Research Topic Identification' : 'Generate Question Wheel'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'research'
              ? 'Select products to analyze for research topic identification'
              : 'Select products to generate question wheels from'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 min-h-0 flex flex-col">
          {/* Search and Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="search">Search Products</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search by product name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="category">Category Filter</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger id="category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Selection Controls */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
                disabled={loading}
              >
                <CheckSquare className="h-4 w-4 mr-2" />
                Select All ({filteredProducts.length})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearAll}
                disabled={loading || selectedProducts.size === 0}
              >
                <Square className="h-4 w-4 mr-2" />
                Clear All
              </Button>
            </div>

            <Badge variant="secondary">
              {selectedProducts.size} product{selectedProducts.size !== 1 ? 's' : ''} selected
            </Badge>
          </div>

          {/* Product List */}
          <ScrollArea className="flex-1 border rounded-md">
            {loading ? (
              <div className="p-4 space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No products found</p>
                {searchTerm && (
                  <p className="text-sm mt-1">Try adjusting your search or filters</p>
                )}
              </div>
            ) : (
              <div className="p-4 space-y-2">
                {filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    className="flex items-start gap-3 p-3 border rounded-md hover:bg-accent cursor-pointer"
                    onClick={() => handleToggleProduct(product.id)}
                  >
                    <Checkbox
                      checked={selectedProducts.has(product.id)}
                      onCheckedChange={() => handleToggleProduct(product.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex-1">
                      <p className="font-medium">{product.product_name}</p>
                      {product.category && (
                        <Badge variant="outline" className="mt-1">
                          {product.category}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          {/* Configuration Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
            <div>
              <Label htmlFor="maxProducts">Max Products to Analyze</Label>
              <Input
                id="maxProducts"
                type="number"
                min={1}
                max={10000}
                value={maxProducts}
                onChange={(e) => setMaxProducts(parseInt(e.target.value) || 1000)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Limit the number of products to analyze
              </p>
            </div>

            <div>
              <Label htmlFor="language">Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger id="language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English (en)</SelectItem>
                  <SelectItem value="es">Spanish (es)</SelectItem>
                  <SelectItem value="fr">French (fr)</SelectItem>
                  <SelectItem value="de">German (de)</SelectItem>
                  <SelectItem value="it">Italian (it)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Language for generated content
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={confirming}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={confirming || selectedProducts.size === 0}
          >
            {confirming
              ? mode === 'research'
                ? 'Starting Research...'
                : 'Generating Wheel...'
              : mode === 'research'
              ? 'Start Research'
              : 'Generate Wheel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
